import { Ellipsis } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type Goal,
  type Routine,
  type SchemaNames,
  type HealthArea,
  SCHEMA_NAMES,
} from "instantdb/types";
import { ConfirmDeleteDialog } from "@/components/dialogs/ConfirmDeleteDialog";
import { useResourceActions } from "@/hooks/resources/useResourceActions";
import { CommonIcon } from "@/icons/CommonIcon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getResourceInfo } from "@/utilities/getResourceInfo";
import { useTranslation } from "@/hooks/useTranslation";

type ResourceDropdownProps = {
  slug: SchemaNames;
  resource: HealthArea | Goal | Routine;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  align?: "start" | "end";
  side?: "top" | "bottom";
  className?: string;
};

/**
 * Generic dropdown menu for resources
 */
export const ResourceDropdown = ({
  slug,
  resource,
  trigger,
  onOpenChange,
  className,
  align = "end",
  side = "bottom",
}: ResourceDropdownProps) => {
  const { labels } = getResourceInfo(slug);
  const { openConfirmDeleteDialog, setConfirmDeleteDialogOpen, actions, handleDelete } =
    useResourceActions(slug, resource);
  const { t, tNode } = useTranslation();

  const title = slug === SCHEMA_NAMES.healthAreas ? t(resource.title) : resource.title;

  return (
    <>
      <ConfirmDeleteDialog
        description={
          <span>
            {tNode("resource_dropdown.delete_confirmation", {
              resource: labels.singular.toLowerCase(),
              title: <strong>{title}</strong>,
            })}
          </span>
        }
        open={openConfirmDeleteDialog}
        onOpenChange={setConfirmDeleteDialogOpen}
        onConfirm={handleDelete}
      />

      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          {trigger ? (
            trigger
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "min-h-10 min-w-10 rounded-full transition-colors bg-muted/60 dark:bg-muted",
                "hover:bg-muted dark:hover:bg-muted/60",
                "active:bg-muted dark:active:bg-muted/60",
                className,
              )}
            >
              <Ellipsis className="size-5" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} side={side} data-no-dnd>
          <DropdownMenuLabel>
            {t("resource_dropdown.actions", { resource: labels.singular })}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {actions.flatMap((action) =>
              [
                <DropdownMenuItem
                  key={action.label}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  <CommonIcon name={action.icon} />
                  <span>{action.label}</span>
                </DropdownMenuItem>,
                action.separator && <DropdownMenuSeparator key={`${action.label}-separator`} />,
              ].filter(Boolean),
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
